import { useState, useEffect, useMemo, useCallback } from "react";
import { authFetch, API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Shield, Download, FileText, Users, Clock, AlertTriangle,
  ChevronRight, RefreshCw, X, Merge as MergeIcon, RotateCcw,
  UserCog, Edit2, Bell, ArrowLeft
} from "lucide-react";

/**
 * AdminIncidentsManager — Phase 35.
 * Three sub-views: Register (list), Detail (audit + actions), Compliance dashboard.
 * Export bar (CSV/Excel/PDF) + notification settings drawer.
 */

const CATEGORY_LABELS = {
  death: "Death of participant",
  serious_injury: "Serious injury",
  abuse_neglect: "Abuse / neglect",
  unlawful_sexual_physical_contact: "Unlawful contact / assault",
  unauthorised_restrictive_practice: "Unauthorised restrictive practice",
  other_internal: "Other (internal)",
};

const STATUS_STYLES = {
  open: "bg-amber-100 text-amber-700",
  under_investigation: "bg-blue-100 text-blue-700",
  awaiting_notification: "bg-purple-100 text-purple-700",
  notified: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-200 text-slate-600",
  reopened: "bg-rose-100 text-rose-700",
};

export function AdminIncidentsManager() {
  const [view, setView] = useState("register"); // register | detail | compliance
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState({ category: "all", status: "all", reportable: "all" });
  const [notifOpen, setNotifOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/incidents`);
    if (r.ok) setIncidents(await r.json());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => incidents.filter((i) => {
    if (filter.category !== "all" && i.category !== filter.category) return false;
    if (filter.status !== "all" && i.status !== filter.status) return false;
    if (filter.reportable === "yes" && !i.is_reportable) return false;
    if (filter.reportable === "no" && i.is_reportable) return false;
    return true;
  }), [incidents, filter]);

  const openDetail = (id) => { setSelectedId(id); setView("detail"); };
  const backToRegister = () => { setSelectedId(null); setView("register"); load(); };

  const exportRegister = async (fmt) => {
    const r = await authFetch(`${API}/admin/incidents/export?format=${fmt}`);
    if (!r.ok) { toast.error("Export failed"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `btr-incident-register-${new Date().toISOString().slice(0,10)}.${fmt === "excel" ? "xlsx" : fmt}`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${fmt.toUpperCase()}`);
  };

  return (
    <div className="space-y-4">
      {/* Header + view switcher */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#5BA3C0]" />
          <h3 className="font-semibold text-slate-900">Incident Management</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button onClick={() => setView("register")} className={`px-3 py-1.5 text-xs font-semibold ${view==="register" ? "bg-[#87CEEB]/15 text-[#5BA3C0]" : "text-slate-600"}`} data-testid="incidents-view-register">Register</button>
            <button onClick={() => setView("compliance")} className={`px-3 py-1.5 text-xs font-semibold ${view==="compliance" ? "bg-[#87CEEB]/15 text-[#5BA3C0]" : "text-slate-600"}`} data-testid="incidents-view-compliance">Compliance</button>
          </div>
          <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
          <Button size="sm" variant="outline" onClick={() => setNotifOpen(true)} data-testid="incidents-notif-settings-btn"><Bell className="w-3.5 h-3.5 mr-1" />Alerts</Button>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button onClick={() => exportRegister("csv")} className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="incidents-export-csv"><Download className="w-3 h-3 inline mr-1" />CSV</button>
            <button onClick={() => exportRegister("excel")} className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 border-l border-slate-200" data-testid="incidents-export-xlsx">Excel</button>
            <button onClick={() => exportRegister("pdf")} className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 border-l border-slate-200" data-testid="incidents-export-pdf">PDF</button>
          </div>
        </div>
      </div>

      {view === "register" && (
        <RegisterView
          incidents={filtered}
          loading={loading}
          filter={filter}
          setFilter={setFilter}
          openDetail={openDetail}
        />
      )}
      {view === "detail" && selectedId && (
        <DetailView incidentId={selectedId} onBack={backToRegister} incidents={incidents} />
      )}
      {view === "compliance" && <ComplianceDashboard />}
      {notifOpen && <NotificationSettingsDrawer onClose={() => setNotifOpen(false)} />}
    </div>
  );
}

/* ------------------------------ Register ------------------------------ */

function RegisterView({ incidents, loading, filter, setFilter, openDetail }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="incidents-filter-category">
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="incidents-filter-status">
          <option value="all">All statuses</option>
          {Object.keys(STATUS_STYLES).map((s) => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
        </select>
        <select value={filter.reportable} onChange={(e) => setFilter({ ...filter, reportable: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="incidents-filter-reportable">
          <option value="all">All (reportable + internal)</option>
          <option value="yes">Reportable only</option>
          <option value="no">Internal only</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left p-3 font-medium">Category</th>
                <th className="text-left p-3 font-medium">Reporter</th>
                <th className="text-left p-3 font-medium">Occurred</th>
                <th className="text-left p-3 font-medium">Deadline</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Notified</th>
                <th className="text-left p-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-400">Loading…</td></tr>}
              {!loading && incidents.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No incidents match your filters.</td></tr>}
              {incidents.map((i) => (
                <tr key={i.incident_id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(i.incident_id)} data-testid={`incident-row-${i.incident_id}`}>
                  <td className="p-3">
                    <div className="font-medium text-slate-900">{CATEGORY_LABELS[i.category] || i.category}</div>
                    {i.is_reportable && <span className="inline-block mt-1 text-[10px] font-bold text-rose-600 uppercase tracking-wider">REPORTABLE</span>}
                  </td>
                  <td className="p-3 text-slate-600">{i.reporter_name}</td>
                  <td className="p-3 text-slate-600">{i.occurred_at?.slice(0,10) || "—"}</td>
                  <td className="p-3 text-slate-600">{i.notification_deadline?.slice(0,10) || "—"}</td>
                  <td className="p-3"><Badge className={STATUS_STYLES[i.status] || "bg-slate-100 text-slate-600"}>{i.status?.replace(/_/g," ")}</Badge></td>
                  <td className="p-3 text-slate-600">{i.commission_notified_at?.slice(0,10) || "—"}</td>
                  <td className="p-3 text-right"><ChevronRight className="w-4 h-4 text-slate-400 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Detail ------------------------------ */

function DetailView({ incidentId, onBack, incidents }) {
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // edit | override | reopen | reassign | merge | note

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/incidents/${incidentId}`);
    if (r.ok) setIncident(await r.json());
    setLoading(false);
  }, [incidentId]);
  useEffect(() => { load(); }, [load]);

  if (loading || !incident) return <div className="text-center py-12 text-slate-400">Loading incident…</div>;

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-[#5BA3C0] font-semibold inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" />Back to register</button>

      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-[#5BA3C0]">Incident {incident.incident_id}</p>
            <h2 className="text-xl font-bold text-slate-900 mt-1">{CATEGORY_LABELS[incident.category] || incident.category}</h2>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge className={STATUS_STYLES[incident.status]}>{incident.status?.replace(/_/g," ")}</Badge>
              {incident.is_reportable && <Badge className="bg-rose-100 text-rose-700">Reportable</Badge>}
              <Badge className="bg-slate-100 text-slate-600">{incident.severity}</Badge>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setModal("edit")} data-testid="detail-edit"><Edit2 className="w-3.5 h-3.5 mr-1" />Edit</Button>
            <Button size="sm" variant="outline" onClick={() => setModal("override")} data-testid="detail-override"><Shield className="w-3.5 h-3.5 mr-1" />Override</Button>
            <Button size="sm" variant="outline" onClick={() => setModal("reassign")} data-testid="detail-reassign"><UserCog className="w-3.5 h-3.5 mr-1" />Reassign</Button>
            <Button size="sm" variant="outline" onClick={() => setModal("note")} data-testid="detail-note"><FileText className="w-3.5 h-3.5 mr-1" />Add note</Button>
            <Button size="sm" variant="outline" onClick={() => setModal("merge")} data-testid="detail-merge"><MergeIcon className="w-3.5 h-3.5 mr-1" />Merge</Button>
            {incident.status === "closed" && <Button size="sm" variant="outline" onClick={() => setModal("reopen")} className="text-rose-600 border-rose-200" data-testid="detail-reopen"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reopen</Button>}
          </div>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-5 text-sm">
          <Field label="Reporter" value={`${incident.reporter_name} (${incident.reporter_type})`} />
          <Field label="Owner" value={incident.owner_admin_id || "Unassigned"} />
          <Field label="Occurred at" value={incident.occurred_at || "—"} />
          <Field label="Date became aware" value={incident.date_became_aware || "—"} />
          <Field label="Notification deadline" value={incident.notification_deadline || "n/a"} />
          <Field label="Commission notified" value={incident.commission_notified_at || "Not yet"} />
          <Field label="Location" value={incident.location || "—"} />
          <Field label="Witnesses" value={incident.witnesses || "—"} />
        </dl>

        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Description</p>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{incident.description}</p>
        </div>

        {incident.admin_notes && (
          <div className="mt-5">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Admin notes</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{incident.admin_notes}</p>
          </div>
        )}

        {incident.addendum?.length > 0 && (
          <div className="mt-5">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Merged records ({incident.addendum.length})</p>
            <ul className="space-y-2">
              {incident.addendum.map((a) => (
                <li key={a.entry_id} className="text-xs bg-slate-50 rounded-lg p-3">
                  <p className="font-semibold text-slate-900">{a.merged_incident_id}</p>
                  <p className="text-slate-600 mt-0.5">Merged by {a.merged_by} · {a.merged_at?.slice(0,19).replace("T", " ")} UTC</p>
                  <p className="text-slate-500 italic mt-1">"{a.reason}"</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Immutable audit feed */}
        <div className="mt-6 border-t border-slate-100 pt-5">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3">Audit trail · immutable</p>
          <ul className="space-y-3" data-testid="audit-trail">
            {(incident.audit_log || []).slice().reverse().map((e) => (
              <li key={e.entry_id} className="text-xs border-l-2 border-slate-200 pl-3 py-1">
                <div className="flex justify-between text-slate-500">
                  <span><strong className="text-slate-900">{e.actor_name || "system"}</strong> {e.action}</span>
                  <span>{e.timestamp?.slice(0,19).replace("T"," ")} UTC</span>
                </div>
                {Object.keys(e.changes || {}).length > 0 && (
                  <ul className="mt-1 text-slate-600">
                    {Object.entries(e.changes).map(([k, v]) => (
                      <li key={k} className="font-mono text-[11px]"><span className="text-slate-400">{k}:</span> {String(v.from ?? "∅")} → {String(v.to)}</li>
                    ))}
                  </ul>
                )}
                {e.reason && <p className="text-[11px] text-slate-500 italic mt-1">Reason: {e.reason}</p>}
                {e.note && <p className="text-[11px] text-slate-500 mt-1">Note: {e.note}</p>}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {modal && (
        <ActionModal
          mode={modal}
          incident={incident}
          incidents={incidents}
          onClose={() => setModal(null)}
          onSaved={(fresh) => { setIncident(fresh); setModal(null); }}
        />
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800 mt-0.5">{value}</dd>
    </div>
  );
}

/* ------------------------------ Action Modals ------------------------------ */

function ActionModal({ mode, incident, incidents, onClose, onSaved }) {
  const titleMap = { edit:"Edit fields", override:"Override category / reportable", reopen:"Reopen closed incident", reassign:"Reassign owner", merge:"Merge duplicate", note:"Add note" };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h4 className="font-semibold text-slate-900">{titleMap[mode]}</h4>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">
          {mode === "edit"     && <EditForm incident={incident} onSaved={onSaved} />}
          {mode === "override" && <OverrideForm incident={incident} onSaved={onSaved} />}
          {mode === "reopen"   && <ReopenForm incident={incident} onSaved={onSaved} />}
          {mode === "reassign" && <ReassignForm incident={incident} onSaved={onSaved} />}
          {mode === "note"     && <NoteForm incident={incident} onSaved={onSaved} />}
          {mode === "merge"    && <MergeForm incident={incident} incidents={incidents} onSaved={onSaved} />}
        </div>
      </div>
    </div>
  );
}

async function callAction(url, body, method = "POST") {
  const r = await authFetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  return { ok: r.ok, data: d };
}

function EditForm({ incident, onSaved }) {
  const [form, setForm] = useState({
    severity: incident.severity || "",
    status: incident.status || "",
    location: incident.location || "",
    witnesses: incident.witnesses || "",
    admin_notes: incident.admin_notes || "",
    date_became_aware: incident.date_became_aware?.slice(0,16) || "",
    commission_notified_at: incident.commission_notified_at?.slice(0,16) || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    const payload = { ...form };
    if (payload.date_became_aware) payload.date_became_aware = new Date(payload.date_became_aware).toISOString();
    if (payload.commission_notified_at) payload.commission_notified_at = new Date(payload.commission_notified_at).toISOString();
    const { ok, data } = await callAction(`${API}/admin/incidents/${incident.incident_id}`, payload, "PUT");
    if (ok) { toast.success("Updated"); onSaved(data); } else toast.error(data.detail || "Failed");
    setSaving(false);
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Severity</Label><select value={form.severity} onChange={(e)=>setForm({...form,severity:e.target.value})} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">{["low","medium","high","critical"].map(s=><option key={s} value={s}>{s}</option>)}</select></div>
        <div><Label>Status</Label><select value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">{Object.keys(STATUS_STYLES).map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}</select></div>
        <div><Label>Date became aware</Label><Input type="datetime-local" value={form.date_became_aware} onChange={(e)=>setForm({...form,date_became_aware:e.target.value})} /></div>
        <div><Label>Commission notified</Label><Input type="datetime-local" value={form.commission_notified_at} onChange={(e)=>setForm({...form,commission_notified_at:e.target.value})} /></div>
      </div>
      <div><Label>Location</Label><Input value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})} /></div>
      <div><Label>Witnesses</Label><Input value={form.witnesses} onChange={(e)=>setForm({...form,witnesses:e.target.value})} /></div>
      <div><Label>Admin notes</Label><Textarea rows={3} value={form.admin_notes} onChange={(e)=>setForm({...form,admin_notes:e.target.value})} /></div>
      <Button onClick={submit} disabled={saving} className="w-full bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="edit-save">{saving?"Saving…":"Save changes"}</Button>
    </div>
  );
}

function OverrideForm({ incident, onSaved }) {
  const [category, setCategory] = useState(incident.category);
  const [reportable, setReportable] = useState(incident.is_reportable ? "yes" : "no");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setSaving(true);
    const { ok, data } = await callAction(`${API}/admin/incidents/${incident.incident_id}/override`, { category, is_reportable: reportable === "yes", reason });
    if (ok) { toast.success("Overridden"); onSaved(data); } else toast.error(data.detail || "Failed");
    setSaving(false);
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Miscategorisation is the #1 audit failure. This change is logged with the reason field forever.</p>
      <div><Label>Category</Label><select value={category} onChange={(e)=>setCategory(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">{Object.entries(CATEGORY_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}</select></div>
      <div><Label>Reportable to Commission?</Label><select value={reportable} onChange={(e)=>setReportable(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm"><option value="yes">Yes</option><option value="no">No</option></select></div>
      <div><Label>Reason *</Label><Textarea rows={3} value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="Explain why this override is being made — recorded in the audit trail." data-testid="override-reason" /></div>
      <Button onClick={submit} disabled={saving||!reason.trim()} className="w-full bg-rose-500 hover:bg-rose-600 text-white" data-testid="override-save">{saving?"Saving…":"Apply override"}</Button>
    </div>
  );
}

function ReopenForm({ incident, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setSaving(true);
    const { ok, data } = await callAction(`${API}/admin/incidents/${incident.incident_id}/reopen`, { reason });
    if (ok) { toast.success("Reopened"); onSaved(data); } else toast.error(data.detail || "Failed");
    setSaving(false);
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Status will revert to "under investigation".</p>
      <div><Label>Reason *</Label><Textarea rows={3} value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="New information emerged, escalation from participant, etc." /></div>
      <Button onClick={submit} disabled={saving||!reason.trim()} className="w-full bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="reopen-save">{saving?"Saving…":"Reopen incident"}</Button>
    </div>
  );
}

function ReassignForm({ incident, onSaved }) {
  const [owner, setOwner] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!owner.trim()) { toast.error("Enter the new owner"); return; }
    setSaving(true);
    const { ok, data } = await callAction(`${API}/admin/incidents/${incident.incident_id}/reassign`, { new_owner_admin_id: owner, reason });
    if (ok) { toast.success("Reassigned"); onSaved(data); } else toast.error(data.detail || "Failed");
    setSaving(false);
  };
  return (
    <div className="space-y-3">
      <div><Label>New owner (admin user_id or name)</Label><Input value={owner} onChange={(e)=>setOwner(e.target.value)} placeholder="e.g. trent / user_abc123" data-testid="reassign-owner" /></div>
      <div><Label>Reason (optional)</Label><Textarea rows={2} value={reason} onChange={(e)=>setReason(e.target.value)} /></div>
      <Button onClick={submit} disabled={saving} className="w-full bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="reassign-save">{saving?"Saving…":"Reassign"}</Button>
    </div>
  );
}

function NoteForm({ incident, onSaved }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!note.trim()) return;
    setSaving(true);
    const { ok, data } = await callAction(`${API}/admin/incidents/${incident.incident_id}/note`, { note });
    if (ok) { toast.success("Note added"); onSaved(data); } else toast.error(data.detail || "Failed");
    setSaving(false);
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Appended to admin notes with timestamp + your name. Logged in audit trail.</p>
      <Textarea rows={5} value={note} onChange={(e)=>setNote(e.target.value)} placeholder="e.g. Called Commission at 14:20 — reference NDIS-2026-12345" data-testid="note-text" />
      <Button onClick={submit} disabled={saving||!note.trim()} className="w-full bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="note-save">{saving?"Saving…":"Add note"}</Button>
    </div>
  );
}

function MergeForm({ incident, incidents, onSaved }) {
  const candidates = incidents.filter((i) => i.incident_id !== incident.incident_id && !i.merged_into);
  const [dupId, setDupId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!dupId || !reason.trim()) { toast.error("Pick a duplicate and enter a reason"); return; }
    setSaving(true);
    const { ok, data } = await callAction(`${API}/admin/incidents/${incident.incident_id}/merge`, { duplicate_incident_id: dupId, reason });
    if (ok) { toast.success("Merged"); onSaved(data); } else toast.error(data.detail || "Failed");
    setSaving(false);
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">The duplicate will be tombstoned; its full record is preserved inside this master incident's addendum log.</p>
      <div><Label>Duplicate incident to merge in *</Label>
        <select value={dupId} onChange={(e)=>setDupId(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="merge-select">
          <option value="">— pick a candidate —</option>
          {candidates.map((i) => <option key={i.incident_id} value={i.incident_id}>{i.incident_id} · {CATEGORY_LABELS[i.category]||i.category} · {i.reporter_name}</option>)}
        </select>
      </div>
      <div><Label>Reason *</Label><Textarea rows={3} value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="Same event, both parties reported independently." /></div>
      <Button onClick={submit} disabled={saving||!dupId||!reason.trim()} className="w-full bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="merge-save">{saving?"Saving…":"Merge"}</Button>
    </div>
  );
}

/* ------------------------------ Compliance ------------------------------ */

function ComplianceDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    authFetch(`${API}/admin/incidents/compliance`).then(async (r) => { if (r.ok) setData(await r.json()); });
  }, []);
  if (!data) return <div className="text-center py-12 text-slate-400">Loading compliance snapshot…</div>;
  const { totals, reportable_timeliness, open_by_age, patterns } = data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="compliance-dashboard">
      <Widget title="Totals — this year" icon={FileText} big={totals.year.count} subtitle={`${totals.month.count} this month · ${totals.quarter.count} this quarter`}>
        <ul className="mt-3 space-y-1 text-xs text-slate-600">
          {Object.entries(totals.year.by_category).map(([k,v]) => (
            <li key={k} className="flex justify-between"><span>{CATEGORY_LABELS[k]||k}</span><span className="font-semibold">{v}</span></li>
          ))}
        </ul>
      </Widget>

      <Widget title="Reportable timeliness" icon={Clock} big={`${reportable_timeliness.on_time}`} subtitle="on time · Commission notifications">
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <MiniStat label="On time" value={reportable_timeliness.on_time} tone="emerald" />
          <MiniStat label="Late"    value={reportable_timeliness.late}    tone="amber" />
          <MiniStat label="Missed"  value={reportable_timeliness.missed}  tone="rose" />
        </div>
        <p className="text-[11px] text-slate-500 mt-3">Average time to notify: <strong>{reportable_timeliness.avg_notification_hours ?? "—"}h</strong></p>
      </Widget>

      <Widget title="Open by age" icon={AlertTriangle} big={open_by_age.total_open} subtitle="incidents currently open">
        <div className="mt-3 space-y-1.5">
          <AgeBar label=">5 days" value={open_by_age.over_5_days} total={open_by_age.total_open} tone="amber" />
          <AgeBar label=">30 days" value={open_by_age.over_30_days} total={open_by_age.total_open} tone="rose" />
        </div>
      </Widget>

      <Widget title="Patterns — by worker" icon={Users} className="lg:col-span-1">
        <PatternList items={patterns.by_worker} emptyLabel="No worker patterns yet" />
      </Widget>
      <Widget title="Patterns — by participant" icon={Users} className="lg:col-span-2">
        <PatternList items={patterns.by_participant} emptyLabel="No participant patterns yet" />
      </Widget>
    </div>
  );
}

function Widget({ title, icon: Icon, big, subtitle, children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-100 p-5 ${className}`}>
      <div className="flex items-center gap-2 mb-1"><Icon className="w-4 h-4 text-[#5BA3C0]" /><p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{title}</p></div>
      {big !== undefined && <p className="text-3xl font-bold text-slate-900 mt-1">{big}</p>}
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      {children}
    </div>
  );
}
function MiniStat({ label, value, tone }) {
  const bg = { emerald:"bg-emerald-50 text-emerald-700", amber:"bg-amber-50 text-amber-700", rose:"bg-rose-50 text-rose-700" }[tone];
  return <div className={`${bg} rounded-lg py-2`}><p className="text-lg font-bold">{value}</p><p className="text-[10px] uppercase tracking-wider">{label}</p></div>;
}
function AgeBar({ label, value, total, tone }) {
  const pct = total ? Math.round((value/total)*100) : 0;
  const bg = tone === "amber" ? "bg-amber-500" : "bg-rose-500";
  return (
    <div>
      <div className="flex justify-between text-[11px] text-slate-600"><span>{label}</span><span>{value}</span></div>
      <div className="h-1.5 rounded-full bg-slate-100"><div className={`${bg} h-full rounded-full`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
function PatternList({ items, emptyLabel }) {
  if (!items?.length) return <p className="text-xs text-slate-400 mt-3">{emptyLabel}</p>;
  return (
    <ul className="mt-3 space-y-1.5 text-sm">
      {items.slice(0,8).map((it) => (
        <li key={it.name} className="flex justify-between items-center">
          <span className="text-slate-700 truncate">{it.name}</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${it.count >= 3 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{it.count}{it.count >= 3 && " ⚠"}</span>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------ Notification Settings ------------------------ */

function NotificationSettingsDrawer({ onClose }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    authFetch(`${API}/admin/incidents/notification-settings`).then(async (r) => { if (r.ok) setSettings(await r.json()); });
  }, []);
  if (!settings) return null;
  const RECIPIENTS = [
    { key: "trent",    label: "Trent" },
    { key: "rashaun",  label: "Rashaun" },
    { key: "beshoy",   label: "Beshoy" },
  ];
  const CHANNELS = [
    { key: "email",   label: "Email" },
    { key: "in_app",  label: "In-app" },
    { key: "sms",     label: "SMS (critical only)" },
  ];
  const toggle = (arr, k) => arr.includes(k) ? arr.filter(x => x !== k) : [...arr, k];
  const save = async () => {
    setSaving(true);
    const r = await authFetch(`${API}/admin/incidents/notification-settings`, { method:"PUT", headers:{"Content-Type":"application/json"}, body: JSON.stringify(settings) });
    if (r.ok) { toast.success("Alerts saved"); onClose(); } else toast.error("Failed to save");
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e)=>e.stopPropagation()}>
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <h4 className="font-semibold text-slate-900">Alert settings</h4>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Recipients</p>
            <div className="space-y-1.5">
              {RECIPIENTS.map(r => (
                <label key={r.key} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="w-4 h-4" checked={settings.recipient_keys.includes(r.key)} onChange={()=>setSettings({...settings, recipient_keys: toggle(settings.recipient_keys, r.key)})} data-testid={`notif-recipient-${r.key}`} />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Channels</p>
            <div className="space-y-1.5">
              {CHANNELS.map(c => (
                <label key={c.key} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="w-4 h-4" checked={settings.channels.includes(c.key)} onChange={()=>setSettings({...settings, channels: toggle(settings.channels, c.key)})} data-testid={`notif-channel-${c.key}`} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="w-4 h-4 mt-0.5" checked={settings.sms_when_critical} onChange={(e)=>setSettings({...settings, sms_when_critical: e.target.checked})} data-testid="notif-sms-critical" />
            <span>Also send SMS when a reportable incident is &lt;2h from its Commission-notification deadline</span>
          </label>
          <Button onClick={save} disabled={saving} className="w-full bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="notif-save">{saving?"Saving…":"Save alert settings"}</Button>
        </div>
      </div>
    </div>
  );
}
